import { useEffect, useState } from 'react';
import { X, Loader2, Sparkles, Search } from 'lucide-react';
import { SOURCING_SECTOR_OPTIONS, SWISS_CANTONS } from '../config/leadTaxonomy';
import { runLeadSourcing, listComuniByCantons, buildSourcingGeoSelection, type SourceLeadsResult } from '../services/leadService';
import { SwissCantonMap } from './SwissCantonMap';

interface Props {
  onClose: () => void;
  onDone: (result: SourceLeadsResult) => void;
}

const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm';

function cantonName(id: string): string {
  return SWISS_CANTONS.find((c) => c.id === id)?.name ?? id;
}

/** Modale per lanciare l'agente di sourcing lead (Zefix/opendata.swiss) su
 * UN settore alla volta, combinabile con cantoni multipli (mappa
 * cliccabile), comuni specifici (checklist per cantone) e CAP per la
 * massima flessibilità di ricerca (18/9/2026, richiesta esplicita: "i
 * cantoni devono essere cumulabili" — prima un singolo select). */
export function SourcingModal({ onClose, onDone }: Props) {
  const [sector, setSector] = useState(SOURCING_SECTOR_OPTIONS[0].id);
  const [selectedCantons, setSelectedCantons] = useState<Set<string>>(() => new Set(['TI']));
  const [comuniByCanton, setComuniByCanton] = useState<Record<string, string[]>>({});
  const [cantonByCity, setCantonByCity] = useState<Record<string, string>>({});
  const [loadingCantons, setLoadingCantons] = useState<Set<string>>(new Set());
  const [selectedCities, setSelectedCities] = useState<Set<string>>(new Set());
  const [citySearch, setCitySearch] = useState<Record<string, string>>({});
  const [postalCode, setPostalCode] = useState('');
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');

  // Estratta da toggleCanton perché serve anche al mount, per il cantone
  // preselezionato di default (TI) — non solo quando l'utente clicca sulla
  // mappa.
  const loadComuniForCanton = async (canton: string) => {
    setLoadingCantons((prev) => new Set(prev).add(canton));
    try {
      const rows = await listComuniByCantons([canton]);
      setComuniByCanton((prev) => ({ ...prev, [canton]: rows.map((r) => r.city) }));
      setCantonByCity((prev) => {
        const next = { ...prev };
        for (const row of rows) next[row.city] = row.canton;
        return next;
      });
    } catch (err) {
      console.error('[SourcingModal] recupero comuni fallito', canton, err);
      setComuniByCanton((prev) => ({ ...prev, [canton]: [] }));
    } finally {
      setLoadingCantons((prev) => {
        const next = new Set(prev);
        next.delete(canton);
        return next;
      });
    }
  };

  useEffect(() => {
    for (const canton of selectedCantons) void loadComuniForCanton(canton);
    // Solo al mount — i toggle successivi caricano i comuni da soli (v.
    // toggleCanton), niente da rieseguire quando selectedCantons cambia.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleCanton = (canton: string) => {
    if (selectedCantons.has(canton)) {
      setSelectedCantons((prev) => {
        const next = new Set(prev);
        next.delete(canton);
        return next;
      });
      // Un comune orfano (il suo cantone non è più selezionato) non ha più
      // senso da tenere selezionato — lo toglie anche dalla checklist.
      setSelectedCities((prev) => {
        const next = new Set(prev);
        for (const city of prev) if (cantonByCity[city] === canton) next.delete(city);
        return next;
      });
      return;
    }

    setSelectedCantons((prev) => new Set(prev).add(canton));
    if (!comuniByCanton[canton]) void loadComuniForCanton(canton);
  };

  // Oltre questa soglia non si carica più la checklist comuni per singolo
  // cantone (evita 20+ chiamate SPARQL simultanee quando si seleziona
  // "Tutta la Svizzera") — con così tanti cantoni insieme lo scopo è
  // comunque coprire l'intero territorio, non restringere a comuni
  // specifici (18/9/2026, introdotto per la prima scansione nazionale).
  const MANY_CANTONS_THRESHOLD = 6;

  const selectAllCantons = () => {
    setSelectedCantons(new Set(SWISS_CANTONS.map((c) => c.id)));
  };

  const clearAllCantons = () => {
    setSelectedCantons(new Set());
    setSelectedCities(new Set());
  };

  const toggleCity = (city: string) => {
    setSelectedCities((prev) => {
      const next = new Set(prev);
      if (next.has(city)) next.delete(city);
      else next.add(city);
      return next;
    });
  };

  const handleRun = async () => {
    setRunning(true);
    setError('');
    try {
      const { cantons, cities } = buildSourcingGeoSelection([...selectedCantons], [...selectedCities], cantonByCity);
      const result = await runLeadSourcing({
        sector,
        cantons,
        cities,
        postalCode: postalCode.trim() || undefined,
      });
      onDone(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ricerca fallita.');
      setRunning(false);
    }
  };

  const sortedSelectedCantons = [...selectedCantons].sort((a, b) => cantonName(a).localeCompare(cantonName(b)));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={running ? undefined : onClose} />
      <div className="relative w-full max-w-6xl h-[92vh] max-h-[92vh] flex flex-col bg-white rounded-2xl shadow-2xl">
        {!running && (
          <button type="button" onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10">
            <X size={20} />
          </button>
        )}
        <div className="px-6 pt-6 pb-2 shrink-0">
          <h2 className="text-lg font-bold text-navy-900">Cerca nuovi lead</h2>
          <p className="text-xs text-gray-500 mt-1">
            Interroga il registro di commercio per settore, uno o più cantoni, comuni specifici e CAP insieme — combina i filtri per la massima flessibilità.
          </p>
        </div>

        <div className="flex-1 flex flex-col px-6 py-3 gap-3 min-h-0">
          <div className="shrink-0">
            <label className="block text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1">Settore</label>
            <select value={sector} onChange={(e) => setSector(e.target.value)} disabled={running} className={`${inputClass} max-w-xs`}>
              {SOURCING_SECTOR_OPTIONS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>

          {/* Mappa e riquadro filtri affiancati, non più impilati — impilati
              l'altezza della mappa (calcolata dal suo rapporto d'aspetto,
              non dal contenitore) finiva per traboccare sopra la checklist
              comuni e intercettarne i click (v. bug fix in SwissCantonMap.tsx,
              18/9/2026, richiesta esplicita di un riquadro dedicato). */}
          <div className="flex-1 flex gap-4 min-h-0 max-h-[440px]">
            <div className="flex-1 min-w-0 flex flex-col min-h-0">
              <div className="flex items-center justify-between mb-1 shrink-0">
                <label className="block text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                  Cantoni ({selectedCantons.size} selezionat{selectedCantons.size === 1 ? 'o' : 'i'})
                </label>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={selectAllCantons} disabled={running} className="text-[10px] font-semibold text-navy-900 underline disabled:opacity-50">
                    Tutta la Svizzera
                  </button>
                  {selectedCantons.size > 0 && (
                    <button type="button" onClick={clearAllCantons} disabled={running} className="text-[10px] text-gray-400 underline disabled:opacity-50">
                      Deseleziona tutto
                    </button>
                  )}
                </div>
              </div>
              <div className="flex-1 min-h-0 border border-gray-200 rounded-xl p-2 bg-gray-50">
                <SwissCantonMap selected={selectedCantons} onToggle={toggleCanton} disabled={running} />
              </div>
            </div>

            <div className="w-52 shrink-0 flex flex-col min-h-0 border-l border-gray-100 pl-4">
              <label className="block text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1 shrink-0">
                Comuni specifici (opzionale)
              </label>
              <div className="flex-1 overflow-y-auto space-y-2 pr-1 min-h-0">
                {sortedSelectedCantons.length === 0 && (
                  <p className="text-xs text-gray-400">Seleziona uno o più cantoni sulla mappa per restringere a comuni specifici — altrimenti si cerca nell'intero cantone.</p>
                )}
                {sortedSelectedCantons.length > MANY_CANTONS_THRESHOLD && (
                  <p className="text-xs text-gray-500">
                    Con {sortedSelectedCantons.length} cantoni selezionati la ricerca copre il loro intero territorio — deseleziona qualche cantone per restringere a comuni specifici.
                  </p>
                )}
                {sortedSelectedCantons.length <= MANY_CANTONS_THRESHOLD && sortedSelectedCantons.map((canton) => {
                  const allCities = comuniByCanton[canton] ?? [];
                  const search = (citySearch[canton] ?? '').toLowerCase();
                  const filtered = search ? allCities.filter((c) => c.toLowerCase().includes(search)) : allCities;
                  return (
                    <div key={canton} className="border border-gray-200 rounded-xl p-2.5">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs font-semibold text-navy-900">{cantonName(canton)}</span>
                        {loadingCantons.has(canton) && <Loader2 size={12} className="animate-spin text-gray-400" />}
                      </div>
                      {!loadingCantons.has(canton) && allCities.length > 0 && (
                        <>
                          <div className="flex items-center gap-1.5 px-2 py-1 border border-gray-200 rounded-lg text-xs text-gray-500 mb-1.5">
                            <Search size={11} className="shrink-0 text-gray-400" />
                            <input
                              value={citySearch[canton] ?? ''}
                              onChange={(e) => setCitySearch((prev) => ({ ...prev, [canton]: e.target.value }))}
                              disabled={running}
                              placeholder="Filtra comuni…"
                              className="outline-none w-full min-w-0"
                            />
                          </div>
                          <div className="max-h-40 overflow-y-auto space-y-0.5 pr-1">
                            {filtered.map((city) => (
                              <label key={city} className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={selectedCities.has(city)}
                                  onChange={() => toggleCity(city)}
                                  disabled={running}
                                />
                                {city}
                              </label>
                            ))}
                            {filtered.length === 0 && <p className="text-xs text-gray-400">Nessun comune trovato.</p>}
                          </div>
                        </>
                      )}
                      {!loadingCantons.has(canton) && allCities.length === 0 && (
                        <p className="text-xs text-gray-400">Nessun comune trovato per questo cantone nel registro.</p>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="shrink-0 pt-3 mt-2 border-t border-gray-100">
                <label className="block text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1">CAP (opzionale)</label>
                <input
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                  disabled={running}
                  placeholder="Es. 69 o 6900"
                  className={inputClass}
                />
                <p className="text-[10px] text-gray-400 mt-1">
                  Per prefisso (es. "69" copre l'intera area di Lugano). Nessun cantone selezionato → default Ticino.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 pb-6 pt-2 shrink-0">
          {error && <p className="text-sm text-red-600 mb-2">{error}</p>}
          <button
            onClick={handleRun}
            disabled={running}
            className="w-full flex items-center justify-center gap-2 py-2 rounded-lg text-white font-semibold bg-navy-900 disabled:opacity-50"
          >
            {running ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {running ? 'Ricerca in corso…' : 'Avvia ricerca'}
          </button>
        </div>
      </div>
    </div>
  );
}
